import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Sparkles, Mail, Phone } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { PhoneInput } from './ui/PhoneInput';
import { validateEmail, validateName, validatePhoneMY } from '../constants/validators';
import { SAMPLE_SIGNUP } from '../constants/sampleData';
import { useLanguage } from './SplitLayout';
interface MerchantSignupProps {
  onContinue: (data: {
    method: 'email' | 'phone';
    contact: string;
    fullName: string;
    country: string;
  }) => void;
}
const TRANSLATIONS = {
  EN: {
    title: 'Create your Maybank Merchant account',
    subtitle: 'Start accepting payments and growing your business with Maybank.',
    demoText: 'Demo mode — auto-fill name, email, phone & password.',
    demoBtn: 'Fill sample data',
    fullName: 'Full Name (as per MyKad/Passport)',
    fullNamePlaceholder: 'e.g. Ahmad bin Abdullah',
    signupWith: 'Sign up with',
    phoneTab: 'Phone Number',
    emailTab: 'Email',
    email: 'Email Address',
    emailPlaceholder: 'you@yourbusiness.com',
    mobile: 'Mobile Number',
    password: 'Password',
    passwordPlaceholder: 'At least 8 characters',
    passwordError: 'Password must be at least 8 characters',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    terms: 'By creating an account, you agree to our',
    tos: 'Terms of Service',
    and: 'and',
    privacy: 'Privacy Policy',
    submit: 'Create Account',
    haveAccount: 'Already have an account?',
    login: 'Log in'
  },
  BM: {
    title: 'Cipta akaun Peniaga Maybank anda',
    subtitle: 'Mula menerima pembayaran dan kembangkan perniagaan anda bersama Maybank.',
    demoText: 'Mod demo — isi automatik nama, e-mel, telefon & kata laluan.',
    demoBtn: 'Isi data sampel',
    fullName: 'Nama Penuh (seperti dalam MyKad/Pasport)',
    fullNamePlaceholder: 'cth. Ahmad bin Abdullah',
    signupWith: 'Daftar dengan',
    phoneTab: 'Nombor Telefon',
    emailTab: 'E-mel',
    email: 'Alamat E-mel',
    emailPlaceholder: 'anda@perniagaananda.com',
    mobile: 'Nombor Telefon Bimbit',
    password: 'Kata Laluan',
    passwordPlaceholder: 'Sekurang-kurangnya 8 aksara',
    passwordError: 'Kata laluan mestilah sekurang-kurangnya 8 aksara',
    showPassword: 'Tunjukkan kata laluan',
    hidePassword: 'Sembunyikan kata laluan',
    terms: 'Dengan mencipta akaun, anda bersetuju dengan',
    tos: 'Terma Perkhidmatan',
    and: 'dan',
    privacy: 'Dasar Privasi',
    submit: 'Cipta Akaun',
    haveAccount: 'Sudah mempunyai akaun?',
    login: 'Log masuk'
  },
  ZH: {
    title: '创建您的马来亚银行商家账户',
    subtitle: '通过马来亚银行开始接受付款并发展您的业务。',
    demoText: '演示模式 — 自动填写姓名、电邮、电话和密码。',
    demoBtn: '填入示例数据',
    fullName: '全名（与身份证/护照一致）',
    fullNamePlaceholder: '例如：Ahmad bin Abdullah',
    signupWith: '注册方式',
    phoneTab: '电话号码',
    emailTab: '电子邮件',
    email: '电子邮件地址',
    emailPlaceholder: 'you@yourbusiness.com',
    mobile: '手机号码',
    password: '密码',
    passwordPlaceholder: '至少 8 个字符',
    passwordError: '密码必须至少 8 个字符',
    showPassword: '显示密码',
    hidePassword: '隐藏密码',
    terms: '创建账户即表示您同意我们的',
    tos: '服务条款',
    and: '和',
    privacy: '隐私政策',
    submit: '创建账户',
    haveAccount: '已有账户？',
    login: '登录'
  }
};
export function MerchantSignup({
  onContinue
}: MerchantSignupProps) {
  const lang = useLanguage();
  const t = TRANSLATIONS[lang];
  const [method, setMethod] = useState<'phone' | 'email'>('phone');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const validate = () => {
    const newErrors: Record<string, string> = {};
    const nameErr = validateName(fullName);
    if (nameErr) newErrors.fullName = nameErr;
    if (method === 'email') {
      const emailErr = validateEmail(email);
      if (emailErr) newErrors.email = emailErr;
    } else {
      const phoneErr = validatePhoneMY(phone);
      if (phoneErr) newErrors.phone = phoneErr;
    }
    if (password.length < 8) {
      newErrors.password = t.passwordError;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onContinue({
      method,
      contact: method === 'phone' ? phone : email,
      fullName,
      country: 'MY'
    });
  };
  const handleFillSample = () => {
    setFullName(SAMPLE_SIGNUP.fullName);
    setEmail(SAMPLE_SIGNUP.email);
    setPhone(SAMPLE_SIGNUP.phone);
    setPassword(SAMPLE_SIGNUP.password);
    setErrors({});
  };
  return <div className="space-y-6 pb-20 lg:pb-0" data-id="element-639">
      <div data-id="element-640">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 leading-tight" data-id="element-641">
          {t.title}
        </h1>
        <p className="text-gray-600" data-id="element-642">{t.subtitle}</p>
      </div>

      {/* Demo helper */}
      <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 border border-dashed border-gray-300" data-id="element-643">
        <div className="flex items-center gap-2 text-xs text-gray-600 min-w-0" data-id="element-644">
          <Sparkles className="w-4 h-4 text-gray-700 flex-shrink-0" aria-hidden="true" data-id="element-645" />
          <span className="truncate" data-id="element-646">{t.demoText}</span>
        </div>
        <button type="button" onClick={handleFillSample} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-900 text-white text-xs font-semibold hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 transition-colors flex-shrink-0" data-id="element-647">
          <Sparkles className="w-3.5 h-3.5" aria-hidden="true" data-id="element-648" />
          {t.demoBtn}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" data-id="element-649">
        {/* Full Name */}
        <Input label={t.fullName} placeholder={t.fullNamePlaceholder} value={fullName} onChange={e => {
        setFullName(e.target.value);
        setErrors(prev => ({
          ...prev,
          fullName: ''
        }));
      }} error={errors.fullName} required data-id="element-650" />

        {/* Method Toggle */}
        <div data-id="element-651">
          <span className="block text-sm font-medium text-gray-900 mb-2" data-id="element-652">
            {t.signupWith}
          </span>
          <div role="radiogroup" aria-label={t.signupWith} className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-lg" data-id="element-653">
            <button type="button" role="radio" aria-checked={method === 'phone'} onClick={() => {
            setMethod('phone');
            setErrors(prev => ({
              ...prev,
              email: '',
              phone: ''
            }));
          }} className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-black ${method === 'phone' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-654">
              <Phone className="w-4 h-4" aria-hidden="true" data-id="element-655" />
              {t.phoneTab}
            </button>
            <button type="button" role="radio" aria-checked={method === 'email'} onClick={() => {
            setMethod('email');
            setErrors(prev => ({
              ...prev,
              email: '',
              phone: ''
            }));
          }} className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-black ${method === 'email' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-656">
              <Mail className="w-4 h-4" aria-hidden="true" data-id="element-657" />
              {t.emailTab}
            </button>
          </div>
        </div>

        {/* Conditional contact field */}
        <AnimatePresence mode="wait" data-id="element-658">
          {method === 'phone' ? <motion.div key="phone" initial={{
          opacity: 0,
          y: -6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.18
        }} data-id="element-659">
              <PhoneInput label={t.mobile} value={phone} onChange={v => {
            setPhone(v);
            setErrors(prev => ({
              ...prev,
              phone: ''
            }));
          }} error={errors.phone} required data-id="element-660" />
            </motion.div> : <motion.div key="email" initial={{
          opacity: 0,
          y: -6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.18
        }} data-id="element-661">
              <Input label={t.email} type="email" inputMode="email" autoComplete="email" placeholder={t.emailPlaceholder} value={email} onChange={e => {
            setEmail(e.target.value);
            setErrors(prev => ({
              ...prev,
              email: ''
            }));
          }} error={errors.email} required data-id="element-662" />
            </motion.div>}
        </AnimatePresence>

        {/* Password */}
        <div className="relative" data-id="element-663">
          <Input id="password" label={t.password} type={showPassword ? 'text' : 'password'} placeholder={t.passwordPlaceholder} value={password} onChange={e => {
          setPassword(e.target.value);
          setErrors(prev => ({
            ...prev,
            password: ''
          }));
        }} error={errors.password} required className="pr-12" data-id="element-664" />
          <button type="button" onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? t.hidePassword : t.showPassword} aria-pressed={showPassword} className="absolute right-3 top-[34px] inline-flex items-center justify-center w-9 h-9 rounded text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-1 transition-colors" data-id="element-665">
            {showPassword ? <EyeOff className="w-5 h-5" aria-hidden="true" data-id="element-666" /> : <Eye className="w-5 h-5" aria-hidden="true" data-id="element-667" />}
          </button>
        </div>

        {/* Footer & Actions */}
        <div className="pt-4 space-y-4" data-id="element-668">
          <p className="text-xs text-gray-500 text-center leading-relaxed" data-id="element-669">
            {t.terms}{' '}
            <a href="#" className="text-gray-900 font-semibold hover:underline" data-id="element-670">
              {t.tos}
            </a>{' '}
            {t.and}{' '}
            <a href="#" className="text-gray-900 font-semibold hover:underline" data-id="element-671">
              {t.privacy}
            </a>
            .
          </p>

          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 lg:static lg:p-0 lg:bg-transparent lg:border-none z-50" data-id="element-672">
            <Button type="submit" fullWidth data-id="element-673">
              {t.submit}
            </Button>

            <div className="mt-4 text-center" data-id="element-674">
              <p className="text-sm text-gray-600" data-id="element-675">
                {t.haveAccount}{' '}
                <a href="#" className="font-semibold text-gray-900 hover:underline underline-offset-2" data-id="element-676">
                  {t.login}
                </a>
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>;
}